import React, { useMemo, useState, forwardRef } from 'react'
import Rating from './Rating.tsx'
import { applyFilter } from '@aytm/app/swidget/base/questions/Ecommerce/utils/applyFilter.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import { getAssetType } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getAssetType.ts'
import { getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets.ts'
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '@aytm/app/swidget/base/questions/Ecommerce/types'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import style from './Card.module.scss'
import AssetsGate from '@aytm/app/swidget/base/questions/Ecommerce/components/AssetsGate.tsx'
import nikeStyle from '../Nike.module.scss'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types.ts'
import { hiddenClass } from '@aytm/app/swidget/base/questions/Ecommerce/utils/hiddenClass.ts'

interface ICardProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  index: number
  disabled?: boolean
  onItemSelect: (index: number) => void
}

const Card = forwardRef<HTMLDivElement, ICardProps>(({ schema, answer, index, onItemSelect, disabled }, ref) => {
  const viewport = useViewport()
  const { t, languageTag } = useSurveyLocale()
  const [showGallery, setShowGallery] = useState<boolean>(false)
  const assetsCollection: assetsCollectionElement[] = useMemo(
    () =>
      [...(answer.image ? [answer.image] : []), ...(answer.gallery || [])].filter(
        asset => ASSETS_TYPES.IMAGE === getAssetType(asset)
      ),
    [answer]
  )
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(0)
  const galleryItemsLeft = Math.max(assetsCollection?.length, 0) - 4 > 0 ? assetsCollection.length - 4 : 0
  const canShowGallery = assetsCollection?.length > 1 && !viewport.mobile
  const gallery = useMemo(
    () =>
      assetsCollection?.length > 0
        ? assetsCollection.map((asset, index) => (index < 4 ? asset : null)).filter(Boolean)
        : [],
    [assetsCollection]
  )

  return (
    <div
      ref={ref}
      id={`${answer.id}`}
      className={`
        ${style.card}
        ${hiddenClass(answer)}
        ${viewport.mobile ? style.cardMobile : ''}
        ${disabled ? style.disabled : ''}
        ${null !== schema.products_per_row && !viewport.mobile ? style[`card${schema.products_per_row}PerRow`] : ''}
      `}
      onMouseEnter={() => {
        if (!showGallery) setShowGallery(true)
      }}
      onMouseLeave={() => {
        if (showGallery) setShowGallery(false)
        setSelectedAssetIndex(0)
      }}
    >
      <div
        className={`${style.image} ${
          !answer.not_apply_filter &&
          assetsCollection[selectedAssetIndex] &&
          ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex]) &&
          applyFilter(assetsCollection[selectedAssetIndex] as IImageAsset)
            ? style.imageFilter
            : ''
        }`}
        onClick={() => onItemSelect(index)}
      >
        {assetsCollection[selectedAssetIndex] &&
        ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex]) ? (
          <AssetsGate
            urls={[getMediumAssetPath(assetsCollection[selectedAssetIndex] as IImageAsset)]}
            loader={<div className={nikeStyle.photoLoading} />}
          >
            <div
              className={style.mainImage}
              role="img"
              aria-label={
                (assetsCollection[selectedAssetIndex] as IImageAsset).alt_text || t('questions', 'img', 'noAlt')
              }
              style={{
                backgroundImage: `url(${getMediumAssetPath(assetsCollection[selectedAssetIndex] as IImageAsset)})`
              }}
            />
          </AssetsGate>
        ) : (
          <div className={style.noImage} />
        )}
      </div>
      <div className={style.content}>
        <div
          onClick={() => onItemSelect(index)}
          className={style.galleryWrapper}
          style={{
            opacity: showGallery && canShowGallery ? 1 : 0,
            visibility: showGallery && canShowGallery ? 'visible' : 'hidden',
            position: showGallery && canShowGallery ? 'static' : 'absolute',
            zIndex: showGallery && canShowGallery ? '1' : '-1',
            width: '100%',
            transition: 'opacity 0.5s ease-in-out, visibility 0.1s ease-in-out'
          }}
        >
          <div className={style.gallery}>
            {gallery.map((asset, index) => (
              <>
                {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
                  <div
                    onMouseEnter={() => setSelectedAssetIndex(index)}
                    className={style.galleryAsset}
                    role="img"
                    aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
                    style={{ backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})` }}
                    key={index}
                  />
                )}
              </>
            ))}
            {galleryItemsLeft > 0 && <div className={style.more}>+{galleryItemsLeft}</div>}
          </div>
        </div>
        <div
          onClick={() => onItemSelect(index)}
          className={style.captionWrapper}
          style={{
            opacity: !(showGallery && canShowGallery) ? 1 : 0,
            visibility: !(showGallery && canShowGallery) ? 'visible' : 'hidden',
            position: !(showGallery && canShowGallery) ? 'static' : 'absolute',
            zIndex: !(showGallery && canShowGallery) ? '1' : '-1',
            width: '100%',
            transition: 'opacity 0.5s ease-in-out, visibility 0.1s ease-in-out'
          }}
        >
          <div
            className={style.caption}
            dangerouslySetInnerHTML={{ __html: answer.text }}
            onClick={() => onItemSelect(index)}
          />
        </div>
        {answer.red_label && <div className={style.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />}
        <div className={style.ratingWrapper}>{answer.rating > 0 && <Rating rating={answer.rating} />}</div>
        <div className={`${style.priceWrapper} ${nikeStyle.prices}`}>
          {answer.discounted_price && answer.price > answer.discounted_price ? (
            <>
              <div
                className={`${nikeStyle.newPrice} ${style.priceMobile}`}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                }}
              />
              <div
                className={`${nikeStyle.lastPrice} ${style.priceMobile}`}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                }}
              />
              {!viewport.mobile && (
                <div
                  className={nikeStyle.off}
                  dangerouslySetInnerHTML={{
                    __html: t<(off: string, applied: boolean) => string>(
                      'questions',
                      'ec',
                      'nike',
                      'off'
                    )(
                      (
                        ((schema.answers[index].price - schema.answers[index].discounted_price) /
                          schema.answers[index].price) *
                        100
                      ).toFixed(1),
                      false
                    )
                  }}
                />
              )}
            </>
          ) : (
            <div
              className={`${nikeStyle.defaultPrice} ${style.priceMobile}`}
              dangerouslySetInnerHTML={{
                __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
})

Card.displayName = 'Card'

export default Card
